/**
 * The Inquiries destination's reads (go-service/internal/api/arrivals.go).
 *
 * Its own file and not settings-api's: this is not settings. Settings
 * edits how the Company's intake behaves; this reads what came in, and
 * the two are different places in the app for the same reason they are
 * different files here.
 *
 * Five reads, one delete and one correction. There is nothing on this
 * surface to save about the job: what a Company does with one happens
 * in the rental system that holds it. What it may do here is throw an
 * arrival away, which is the RMS's own events-list delete brought
 * across, and put right what a call's summary says (#89).
 */
import { apiFetch, apiFetchBlob } from "../../../shared/api-client";

/**
 * One arrival: a Phone Call, or an Inquiry no call produced.
 *
 * The API sends the vocabulary and not sentences — `arrived` is
 * CONTEXT.md's own Phone Call source with "form" beside it, and
 * `inquiry_status` and `failure_reason` are the Inquiry's own — because
 * a word chosen in Go is a word that cannot be changed without a deploy.
 */
export interface Arrival {
  /** Which of the two this row is, and which route opens it. */
  kind: "call" | "form";
  id: string;
  /** When it came in, ISO-8601 in UTC; null on a row that never started. */
  at: string | null;
  /** The caller's number, or the number a form submitter left. */
  from_e164: string;
  /**
   * Their name: the one on the Inquiry, or the one they gave on the
   * call. Empty where nobody ever said one.
   */
  who: string;
  /** answered_call | voicemail | receptionist | form. */
  arrived: string;
  /**
   * What was said: the sentence a search matched in, or the opening
   * line. Never the whole Transcript — that is what opening a row is
   * for.
   */
  said: string;
  has_recording: boolean;
  became_inquiry: boolean;
  /** The Inquiry's status, or "" where the arrival became none. */
  inquiry_status: string;
  failure_reason: string;
  quote_number: string;
  /** Where the job is, as the Backend itself authored the link. */
  workspace_url: string;
  /**
   * What was said, in a sentence or two (#89): the one text a call has
   * (#119), read off the Transcript by the pipeline, or written by the
   * Company. The list and the thread show it cut short; the call itself
   * shows the whole of it. Empty on a form submission, and on a call
   * nobody could summarise.
   */
  summary: string;
  /**
   * Where they said they were ringing from ("Mellons Bay School"), off
   * the Transcript. The line under the name, never in place of it.
   */
  who_org: string;
  /**
   * The job this arrival is about (#115), as the Company's rental
   * system names it: the one the Inquiry drafted, or the one the caller
   * picked off the spoken list. Every row about one job carries the
   * same value, and the list groups on it. Opaque and never drawn — the
   * quote number is what a Company calls the job. Empty for a row about
   * no job: a wrong number, a form that failed to draft, a call from
   * before the pick was kept. Empty is a loose row, never a group.
   */
  backend_ref: string;
}

/**
 * One row of the list (#115): an arrival, and where it is about a job,
 * the job. The row is the Inquiry that drafted the job, or its oldest
 * call where the job has no Inquiry here, and the rest of the rows
 * about it are counted rather than drawn — the job page is where they
 * are read. A row with no ref is a job of one and every count on it is
 * what it always was.
 */
export interface ListRow extends Arrival {
  /**
   * What the job is called, when it is and where, off the Inquiry.
   * Empty on a row that became none.
   */
  event_name: string;
  /** YYYY-MM-DD, or "" where no date was given. */
  event_date: string;
  venue: string;
  /**
   * How many calls are about this job, every one of them whether or
   * not a filter matched it; 0 on a row about no job.
   */
  calls: number;
  /**
   * When the newest of them came in, which is what the list is ordered
   * by; the row's own `at` on a row about no job.
   */
  last_at: string | null;
}

/**
 * What the Company calls the rental system its jobs draft into, as the
 * server resolves it from `backend_type`: "Current RMS", "HireOS", or
 * "your rental system" where there is none to name.
 *
 * It rides on the opened arrival and not the list row, because the
 * Outcome's link out is the only thing that says it.
 */
interface OpenedArrival {
  rental_system: string;
  /**
   * The Inquiry's one paragraph (#119): what they asked for, as the
   * form's own words or the call's summary as it stood when the Inquiry
   * was written. Built by the server from the stored Inquiry, exactly as
   * the job's description in the rental system is, and empty on a call
   * that became no Inquiry.
   *
   * On a call it is the whole of the Inquiry this page draws (#119):
   * the Transcript is the record. A form's facts and answers ride
   * beside it, below.
   */
  asked_for: string;
  /**
   * How they asked to be got back to, and when — "by text, after 5pm" —
   * in the words the New Inquiry email and the job's description use,
   * and "" where they said nothing (CONTEXT.md's Contact Preference).
   */
  get_back_to: string;
  /**
   * Everything a form collected, on a form arrival alone: the event's
   * facts as the job's description labels them (organisation, event,
   * date, venue, time, attendance, indoor/outdoor, venue contact, site
   * visit), in that order, with only what was given. The page is where
   * an enquiry waiting for Send is read before anything reaches the
   * rental system. Absent on a call, whose Transcript is the record.
   */
  facts?: NoteRow[];
  /**
   * The Profile's own questions as the Company wrote them, with what was
   * answered — Yes or No, or the choice in words — in the Profile's
   * order. Unanswered questions are left out. Absent on a call.
   */
  answers?: NoteRow[];
}

/** One row of the notes: a label and the answer in words. */
export interface NoteRow {
  label: string;
  value: string;
}

/**
 * One speaker's turn and where it sits on the Call Recording (#118),
 * as the server holds it. The transcript text is rendered from these,
 * so the two never disagree; a call transcribed before #118 has the
 * text and no turns.
 */
export interface TranscriptTurn {
  /** Caller, Operator or Receptionist, as the transcriber labelled it. */
  speaker: string;
  text: string;
  /** Milliseconds from the start of the recording. */
  start_ms: number;
  /**
   * Where it ends, on a turn transcribed since the end was asked for.
   * Absent before that, and the turn runs to the next turn's start.
   */
  end_ms?: number;
}

/** One call opened: what it was about, and the whole of what was said. */
export interface CallArrival extends Arrival, OpenedArrival {
  kind: "call";
  /**
   * The enquiry the call was written onto, which Send names; "" on a
   * call that became none.
   */
  inquiry_id: string;
  to_e164: string;
  status: string;
  duration_secs: number | null;
  transcript: string;
  /** The transcript with its timing (#118); null where it has none. */
  turns: TranscriptTurn[] | null;
  discard_reason: string;
  /**
   * Whether the caller left a message, and who for, whatever else the
   * call was: a caller chasing the quote on their job left one about it.
   */
  left_message?: boolean;
  message_for?: string;
  /**
   * The person's email: typed on the call, read off the Transcript, or
   * the Inquiry's, in that order. Empty where nobody has one — an
   * Operator who answered may not have asked.
   */
  email: string;
}

/** One form submission opened. There was never a call. */
export interface FormArrival extends Arrival, OpenedArrival {
  kind: "form";
  contact_email: string;
}

export interface ArrivalsPage {
  arrivals: ListRow[];
  total: number;
  limit: number;
  offset: number;
  /** What was searched for, as the server read it. */
  q: string;
}

/** What the filter row may narrow the list to. Closed on both sides. */
export interface ArrivalFilters {
  q: string;
  arrived: string;
  became: string;
  from: string;
  to: string;
}

/**
 * One page of what came in, or of the arrivals matching the filters.
 *
 * The search is sent as typed. It is matched as literal letters against
 * what was said and who said it, and as digits against either number,
 * so there is nothing here to quote or escape — and the total that comes
 * back counts what was found rather than the log, so a filtered page can
 * say where it sits the same way an unfiltered one does.
 */
export async function listArrivals(
  offset: number,
  limit: number,
  filters: ArrivalFilters,
  signal?: AbortSignal,
): Promise<ArrivalsPage> {
  const page = await apiFetch<ArrivalsPage>("/api/admin/arrivals", {
    query: {
      offset: String(offset),
      limit: String(limit),
      q: filters.q || undefined,
      arrived: filters.arrived || undefined,
      became: filters.became || undefined,
      from: filters.from || undefined,
      to: filters.to || undefined,
    },
    signal,
  });
  return { ...page, arrivals: page.arrivals ?? [] };
}

export async function getCallArrival(
  id: string,
  signal?: AbortSignal,
): Promise<CallArrival> {
  const res = await apiFetch<{ arrival: CallArrival }>(
    `/api/admin/arrivals/call/${encodeURIComponent(id)}`,
    { signal },
  );
  return res.arrival;
}

export async function getFormArrival(
  id: string,
  signal?: AbortSignal,
): Promise<FormArrival> {
  const res = await apiFetch<{ arrival: FormArrival }>(
    `/api/admin/arrivals/form/${encodeURIComponent(id)}`,
    { signal },
  );
  return res.arrival;
}

/**
 * The audio, as bytes.
 *
 * A blob rather than an <audio src>: /api/admin needs the Authorization
 * header a browser fetching a media URL would not send, which is also why
 * the recording is not served by the public file viewer, where the
 * storage ref is itself the credential.
 */
export async function fetchCallRecording(
  id: string,
  signal?: AbortSignal,
): Promise<Blob> {
  const { blob } = await apiFetchBlob(
    `/api/admin/arrivals/call/${encodeURIComponent(id)}/recording`,
    { signal },
  );
  return blob;
}

/** What the Company may change on a call: each key sent is written. */
export type CallEdit = { summary?: string; email?: string };

/**
 * The Company's own hand on one call (#89): its summary and the
 * person's email.
 *
 * Each field commits itself and sends only itself. What the pipeline
 * wrote is a starting point and this replaces it for good: a field the
 * Company has written is never written again by anything. What comes
 * back is the row as it now stands, tidied the way the pipeline tidies
 * its own, so the screen shows what the row holds rather than what was
 * typed.
 */
export async function editCallArrival(
  id: string,
  edit: CallEdit,
): Promise<{ summary: string; email: string }> {
  return apiFetch<{ summary: string; email: string }>(
    `/api/admin/arrivals/call/${encodeURIComponent(id)}`,
    { method: "PATCH", body: edit },
  );
}

/**
 * Send an open enquiry to the Company's rental system: the one way an
 * enquiry the Receptionist took gets there. The server drafts it as it
 * drafts a form's. A 409 is an enquiry somebody already sent or closed.
 */
export async function sendInquiry(inquiryId: string): Promise<{ status: string }> {
  return apiFetch<{ status: string }>(
    `/api/admin/arrivals/inquiry/${encodeURIComponent(inquiryId)}/send`,
    { method: "POST" },
  );
}

/**
 * Close an open enquiry as not an enquiry: nothing reaches the rental
 * system, and the calls and texts under it stay where they are.
 */
export async function closeInquiry(inquiryId: string): Promise<{ status: string }> {
  return apiFetch<{ status: string }>(
    `/api/admin/arrivals/inquiry/${encodeURIComponent(inquiryId)}/close`,
    { method: "POST" },
  );
}

/** What names one arrival to the API: the kind and the id, as the list carries them. */
export type ArrivalRef = Pick<Arrival, "kind" | "id">;

/**
 * Delete what the Company selected: arrivals by kind and id, and jobs
 * by their Backend Ref (#115).
 *
 * A call takes the Inquiry it produced with it, because the row they saw
 * was one thing, and a job takes every arrival about it, because the
 * row they saw was the job; the job at the rental system is not
 * touched. A row that is not there is not counted and is not an error
 * — a stale page deleting a row another tab already removed reads as
 * one fewer. The answer is how many went.
 */
export async function deleteArrivals(
  refs: ArrivalRef[],
  jobs: string[] = [],
): Promise<{ deleted: number }> {
  return apiFetch<{ deleted: number }>("/api/admin/arrivals", {
    method: "DELETE",
    body: {
      arrivals: refs.map(({ kind, id }) => ({ kind, id })),
      jobs,
    },
  });
}

/**
 * One thing in a job's thread (#115): a call about the job, or the form
 * submission the job was drafted from. A call carries what a call
 * carries — its summary, the whole Transcript, whether there is a
 * recording to play; a form submission carries the Customer's own
 * words as `said` and nothing a call has.
 */
export interface JobEntry {
  kind: "call" | "form";
  id: string;
  at: string | null;
  /** answered_call | voicemail | receptionist | form. */
  arrived: string;
  who: string;
  who_org: string;
  from_e164: string;
  email: string;
  summary: string;
  transcript: string;
  /** The transcript with its timing (#118); null where it has none. */
  turns: TranscriptTurn[] | null;
  has_recording: boolean;
  duration_secs: number | null;
  status: string;
  discard_reason: string;
  /** Whether the caller left a message, and who for. A call's alone. */
  left_message?: boolean;
  message_for?: string;
  /**
   * The one entry the job was drafted from — the form submission, or
   * the call that produced the Inquiry — whose paragraph the job's
   * description was written from.
   */
  is_inquiry: boolean;
  /** A form submission's own words. Empty on a call. */
  said: string;
}

/**
 * The Inquiry that drafted a job, built by the server from the stored
 * Inquiry exactly as the arrival page's record is. A form's facts and
 * answers are drawn in the notes; a call's Inquiry carries none, its
 * Transcript being the record (#119). `kind` and `id` name the entry in the
 * thread it is.
 */
export interface JobInquiry extends OpenedArrival {
  kind: "call" | "form";
  id: string;
  /** The enquiry itself, where `id` is the arrival it is read as: what Send names. */
  inquiry_id: string;
  at: string | null;
  contact_email: string;
  contact_phone: string;
}

/**
 * One job as intake knows it (#115): the Inquiry that drafted it and
 * every call about it, as one thread, oldest first. The job itself is
 * the rental system's — its lines, its price, what an Operator has
 * accepted on it — and this holds one link to it and nothing it holds.
 */
export interface Job {
  backend_ref: string;
  /**
   * What the job is called: the Inquiry's event name. Empty for a job
   * intake never drafted, or one whose Inquiry gave none.
   */
  title: string;
  /** The Customer: the Inquiry's contact, else whoever rang. */
  who: string;
  quote_number: string;
  workspace_url: string;
  rental_system: string;
  inquiry_status: string;
  failure_reason: string;
  /**
   * Null where the job was created at the rental system and only rung
   * about.
   */
  inquiry: JobInquiry | null;
  thread: JobEntry[];
}

/** One job: 404 where no arrival of this Company's is about it. */
export async function getJob(ref: string, signal?: AbortSignal): Promise<Job> {
  const res = await apiFetch<{ job: Job }>(
    `/api/admin/jobs/${encodeURIComponent(ref)}`,
    { signal },
  );
  return { ...res.job, thread: res.job.thread ?? [] };
}
